import { availability } from "./availability";
import { bookingForm } from "./booking-form";

/** Actions screen (tenant/actions.html, P6 group `build`). */
export const actions = {
  title: "Actions",
  subtitle: "Actions let the assistant do things, not just answer — book appointments, save leads, look up a status in your systems.",
  tryPlayground: "Try in Playground",
  newAction: "New action",
  kpi: {
    active: "Active actions",
    runs: "Runs (7 days)",
    runsDelta: "{{value}} vs last week",
    successRate: "Success rate",
    failed_one: "{{count}} failed",
    failed_other: "{{count}} failed",
    mostly: "{{count}} failed · mostly {{reason}}",
    median: "Median duration",
    p95: "p95 {{value}}",
    none: "No runs yet",
  },
  filter: {
    label: "Filter actions",
    all: "All · {{count}}",
    builtin: "Built-in · {{count}}",
    custom: "Custom & integrations · {{count}}",
    search: "Search actions",
  },
  card: {
    toggle: "Enable {{name}}",
    toggleLocked: "{{name}} is always on",
    alwaysOn: "Always on",
    runs: "{{count}} runs · 7d",
    off: "Off",
    requiresIdentity: "Requires verified identity",
    builtinLeads: "Built-in → Leads",
    builtinInbox: "Built-in → Inbox",
    builtinEmail: "Built-in email",
    builtinBooking: "Built-in booking",
    builtin: "Built-in",
    integration: {
      custom_api: "Custom API",
      webhook: "Webhook",
      zapier: "Zapier",
    },
    enabled: "{{name}} enabled",
    disabled: "{{name}} disabled",
    routing: {
      label: "Handoffs go to",
      team: "{{name}} team",
      person: "{{name}}",
      fallback: "Owner and Admins",
      payment: "Payment topics: {{name}}",
      change: "Handoffs go to {{target}}. Change in Escalation & handoff settings",
    },
    booking: {
      calendar: "Google Calendar",
      request: "Staff confirm",
      unconfigured: "Set opening hours",
    },
  },
  empty: {
    title: "No actions yet",
    body: "Connect the assistant to your systems: look up a status, create a ticket or send data to a webhook.",
    noMatch: "No actions match your filter",
    noMatchBody: "Try another search or show all actions.",
  },
  templates: {
    title: "New action",
    subtitle: "Start from a template. You can change everything later.",
    custom_api: "Custom API",
    custom_apiBody: "Call your own system or any REST endpoint",
    webhook: "Webhook",
    webhookBody: "POST conversation data to your server",
    zapier: "Zapier",
    zapierBody: "Trigger a Zap with a catch hook URL",
    booking: "Booking",
    bookingBody: "Let visitors book a tour, demo or appointment in chat",
    bookingName: "Book an appointment",
    bookingCreated: "Booking action created. Set your hours and form below.",
  },
  editor: {
    editTitle: "Edit action",
    newTitle: "New: {{template}}",
    kicker: "Action",
    name: "Name",
    toolName: "Tool name (for the AI)",
    toolNameHint: "Lowercase letters, numbers and underscores.",
    toolNameInvalid: "Use lowercase letters, numbers and underscores (2–63 characters, starting with a letter).",
    nameRequired: "Enter a name.",
    whenToUse: "When to use",
    whenToUseHint: "The AI reads this to decide when to call the action.",
    whenToUseCount: "{{used}} / {{max}} characters",
    whenToUseHelp: {
      summary: "How to write a good description",
      intro: "This text is the main thing the AI reads when it chooses an action. Cover these four points:",
      p1: "What the action does and what it gives back.",
      p2: "When to use it, in the words a visitor would use.",
      p3: "When NOT to use it, and which other action to use instead.",
      p4: "The details the visitor must give (an item code, say). If one is missing, the AI should not call it.",
      exampleTitle: "Example: list the items in a store",
      example: "Lists the items in our store with their names and prices. Use when the visitor asks what we sell, what is available, or to see the catalogue. Do NOT use to look up one item by its code (use Get item) or to place an order (use Create order).",
    },
    whenRequired: "Describe when the assistant should use this action.",
    whenTooLong: "Keep it within {{max}} characters.",
    builtinNote: "Built-in action: you can rename it, change the guidance and switches, or turn it off. Its parameters are managed by the platform.",
    parameters: "Parameters",
    addParameter: "Add parameter",
    paramName: "Name",
    paramType: "Type",
    paramRequired: "Required",
    paramDescription: "Description",
    paramRemove: "Remove parameter {{name}}",
    paramsNone: "No parameters: the assistant sends no details, which is right for a plain list. Add one if the endpoint needs input, such as an item code.",
    paramPlaceholder: "param_name",
    paramDescriptionPlaceholder: "What the AI should put here",
    paramInvalid: "Parameter names must be unique and start with a letter or underscore.",
    editJson: "Edit as JSON",
    editTable: "Edit as table",
    jsonLabel: "Parameter JSON Schema",
    jsonInvalid: "This isn't a valid JSON Schema object using the supported keywords.",
    types: { string: "string", number: "number", integer: "integer", boolean: "boolean", date: "date" },
    http: "HTTP request",
    method: "Method",
    url: "URL",
    urlHint: "Use {{example}} placeholders in the path or query. The host can't be templated.",
    urlInvalid: "Enter an https:// URL. Placeholders must match parameters and can't be in the host.",
    headers: "Headers",
    addHeader: "Add header",
    headerName: "Header name",
    headerValue: "Header value",
    headerRemove: "Remove header {{name}}",
    auth: "Authentication",
    authTypes: { none: "None", api_key: "API key (header)", bearer: "Bearer token", hmac: "HMAC signature" },
    authHeader: "Header name",
    secret: "Secret",
    secretHint: "Encrypted at rest. Never shown to the model.",
    secretSaved: "Saved — leave blank to keep it",
    secretRequired: "Enter the credential for this authentication type.",
    timeout: "Timeout (ms)",
    timeoutHint: "Between 1,000 and 30,000 ms. On timeout the assistant falls back gracefully.",
    mapping: "Response mapping",
    mappingHint: "Only mapped fields are passed back to the assistant. Without a mapping it receives the whole response; a long list is cut to the items that fit and marked as partial.",
    mappingListHint: "Use [*] for every item of a list: {{example}} gives the list of names (up to {{max}}). A second [*] flattens nested lists.",
    mappingFieldPlaceholder: "names",
    mappingPathPlaceholder: "$.items[*].name",
    mappingField: "Field",
    mappingPath: "JSON path",
    addMapping: "Add mapping",
    mappingRemove: "Remove mapping {{name}}",
    mappingInvalid: "Fields need a name and a path like $.data.status or $.items[*].name. The name _truncated is reserved.",
    confirm: "Require confirmation before running",
    confirmHint: "The assistant asks the user before it runs the action.",
    confirmHintRead: "Off by default for GET: a read-only lookup (like listing items) runs straight away. Turn it on to show a confirm card first. The Playground always asks.",
    confirmHintWrite: "On by default for POST, PUT and PATCH, which change something on the other side. Turn it off only if running without asking is safe. The Playground always asks.",
    identity: "Requires verified identity",
    identityHint: "Only runs when the widget passes a signed user token (Widget Studio → Security). Identity fields come from the token, never from chat.",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save action",
    saved: "Action saved · available to the assistant now",
    created: "Action created",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "The assistant can no longer call this action. Its run history is deleted too.",
    deleteConfirm: "Type the action name to confirm",
    deleted: "Action deleted",
    readOnly: "You can view this action but not change it.",
    audience: "Who can use this action",
    audienceHint: "Visitors outside these levels never see this action. The agent's own “Who can chat” limit ({{levels}}) always applies on top.",
    audienceEveryone: "Talk to a person is always available to everyone, so its audience can't be changed.",
    audienceRequired: "Pick at least one audience.",
  },
  test: {
    title: "Test",
    live: "Runs against the live endpoint",
    input: "Sample input (JSON)",
    run: "Run test",
    invalidJson: "Sample input is not valid JSON.",
    saveFirst: "Save the action to run a test.",
    builtin: "Built-in actions run inside conversations. Try them in the Playground.",
    ok: "{{status}} OK",
    status: "HTTP {{status}}",
    failed: "Not sent",
    meta: "{{duration}} · {{bytes}} B",
    response: "Response",
    receives: "What the assistant receives",
    errors: "Problems",
  },
  runs: {
    title: "Recent action runs",
    subtitle: "Last 8 runs · inputs are redacted per your privacy settings",
    export: "Export CSV",
    exportHint: "Exports every confirmed or finished run of the last 7 days, with inputs and outputs redacted.",
    exported: "Exported {{name}}",
    time: "Time",
    action: "Action",
    conversation: "Conversation",
    status: "Status",
    duration: "Duration",
    openConversation: "Open conversation",
    noConversation: "—",
    empty: "No runs yet",
    emptyBody: "Runs appear here when the assistant calls an action in a conversation.",
    statuses: {
      succeeded: "Success",
      failed: "Failed",
      timed_out: "Timed out",
      cancelled: "Cancelled",
      pending_confirmation: "Awaiting confirmation",
      expired: "Expired",
      running: "Running",
    },
  },
  planFeature: "Custom actions",
  availability,
  bookingForm,
} as const;
